import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  atlasErrorMessage,
  getAtlasConnection,
  probeAtlasRepo,
  saveAtlasRepo,
  type AtlasRepoProbe,
} from '../../utils/skillsAtlasApi';
import './CompanyBrainRepoCard.css';

const ORG_REPO = 'LC-Timberframes/company_brain';

const CompanyBrainRepoCard: React.FC = () => {
  const [draft, setDraft] = useState(ORG_REPO);
  const [tokenDraft, setTokenDraft] = useState('');
  const [savedRepo, setSavedRepo] = useState<string | null>(null);
  const [source, setSource] = useState<'crm' | 'env' | 'default' | null>(null);
  const [canEdit, setCanEdit] = useState(true);
  const [probe, setProbe] = useState<AtlasRepoProbe | null>(null);
  const [hasToken, setHasToken] = useState(false);
  const [tokenHint, setTokenHint] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getAtlasConnection()
      .then(res => {
        setSavedRepo(res.repo);
        setSource(res.source);
        setCanEdit(res.canEdit);
        setProbe(res.probe);
        setDraft(res.repo || ORG_REPO);
        setHasToken(!!res.hasGithubToken);
        setTokenHint(res.githubTokenHint || null);
      })
      .catch(e => setError(atlasErrorMessage(e)))
      .finally(() => setLoading(false));
  }, []);

  const onTest = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await probeAtlasRepo(draft || ORG_REPO, tokenDraft.trim() || undefined);
      setProbe(res);
    } catch (e) {
      setError(atlasErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onSave = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await saveAtlasRepo(draft || ORG_REPO, tokenDraft.trim() || undefined);
      setSavedRepo(res.repo);
      setSource('crm');
      setProbe(res.probe);
      setDraft(res.repo);
      setHasToken(!!res.hasGithubToken);
      setTokenHint(res.githubTokenHint || tokenHint);
      setTokenDraft('');
    } catch (e) {
      setError(atlasErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="brain-repo-card atlas-connect-card">
      <h3>CompanyBrain GitHub repo</h3>
      <p>
        Company Brain reads{' '}
        <code>{savedRepo || ORG_REPO}</code>
        {source === 'default' ? ' (org default)' : source === 'env' ? ' (from Functions env)' : ''}.
        Use the full repo — <code>{ORG_REPO}</code> — not just the org URL.
      </p>
      {loading ? (
        <p>Checking connection…</p>
      ) : (
        <>
          <label className="brain-repo-label">
            Repo
            <input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder={ORG_REPO}
              aria-label="CompanyBrain GitHub repo"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <label className="brain-repo-label">
            GitHub token
            <input
              type="password"
              value={tokenDraft}
              onChange={e => setTokenDraft(e.target.value)}
              placeholder={hasToken ? `Saved (${tokenHint}) — paste a new one to replace` : 'ghp_… or github_pat_…'}
              aria-label="GitHub personal access token"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <p className="brain-repo-help">
            Fine-grained PAT with Contents read and write on {ORG_REPO}. Stored in a server-only CRM secret — never committed to git, never readable from the client.
          </p>
          <div className="brain-repo-row">
            <button type="button" className="atlas-btn" disabled={busy || !draft.trim()} onClick={() => void onTest()}>
              {busy ? 'Checking…' : 'Test GitHub'}
            </button>
            {canEdit && (
              <button
                type="button"
                className="atlas-btn primary"
                disabled={busy || !draft.trim()}
                onClick={() => void onSave()}
              >
                Save repo{tokenDraft.trim() ? ' and token' : ''}
              </button>
            )}
          </div>
          {!canEdit && <p>Ask an admin to save the repo and token after a successful test.</p>}
          {error && <p className="brain-repo-msg bad">{error}</p>}
          {probe && (
            <p className={`brain-repo-msg ${probe.ok ? 'ok' : 'bad'}`}>
              {probe.message}
              {probe.htmlUrl && (
                <>
                  {' '}
                  <a href={probe.htmlUrl} target="_blank" rel="noreferrer">
                    Open on GitHub <ExternalLink size={12} style={{ verticalAlign: 'middle' }} />
                  </a>
                </>
              )}
            </p>
          )}
        </>
      )}
    </article>
  );
};

export default CompanyBrainRepoCard;
